import React, { useEffect, useState } from 'react';
import { api } from '@/api/client';
import { formatApiError } from '@/utils/formatApiError';
import AppConfirmDialog from '@/ui/components/AppConfirmDialog';

type Props = {
  olderThanDays: number;
  debugOnly: boolean;
  onClose: () => void;
  onDone: (message: string) => void;
};

export default function PipelineClearHistoryModal({
  olderThanDays,
  debugOnly,
  onClose,
  onDone,
}: Props) {
  const [matched, setMatched] = useState<number | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoadingPreview(true);
    setError('');
    void api.searchExecutions
      .cleanupPreview({ older_than_days: olderThanDays, debug_only: debugOnly })
      .then((result) => {
        if (!cancelled) setMatched(result.matched);
      })
      .catch((e) => {
        if (!cancelled) setError(formatApiError(e, 'Failed to load preview'));
      })
      .finally(() => {
        if (!cancelled) setLoadingPreview(false);
      });
    return () => {
      cancelled = true;
    };
  }, [olderThanDays, debugOnly]);

  async function confirm() {
    setSubmitting(true);
    setError('');
    try {
      const result = await api.searchExecutions.cleanup({
        older_than_days: olderThanDays,
        debug_only: debugOnly,
      });
      onDone(`Removed ${result.deleted ?? 0} execution record(s)`);
      onClose();
    } catch (e) {
      setError(formatApiError(e, 'Cleanup failed'));
    } finally {
      setSubmitting(false);
    }
  }

  const scopeLabel = debugOnly ? 'DEBUG search execution' : 'search execution';

  return (
    <AppConfirmDialog
      title="Clear history?"
      lead={
        loadingPreview ? (
          'Counting matching execution records…'
        ) : (
          <>
            Permanently remove <strong>{matched ?? 0}</strong> {scopeLabel} record(s) older than{' '}
            <strong>{olderThanDays}</strong> day{olderThanDays === 1 ? '' : 's'}. Pipeline diagnostics and linked
            activity log rows for those executions are deleted. Discovered listings are kept.
          </>
        )
      }
      confirmLabel={submitting ? 'Removing…' : 'Clear history'}
      submitting={submitting || loadingPreview}
      error={error}
      onClose={onClose}
      onConfirm={confirm}
    />
  );
}
